import { api } from './api';

const ACCESS_TOKEN_KEY = 'viewhub_access_token';
const REFRESH_TOKEN_KEY = 'viewhub_refresh_token';

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface TwoFactorPayload {
  email: string;
  code: string;
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  isAdmin: boolean;
  twoFactorEnabled: boolean;
  role?: {
    id: string;
    name: string;
    permissions: string[];
  };
  workspaces?: Array<{
    id: string;
    name: string;
    slug: string;
  }>;
}

export interface LoginResponse {
  tokens?: {
    accessToken: string;
    refreshToken: string;
  };
  user?: AuthUser;
  requiresTwoFactor?: boolean;
}

class AuthService {
  getAccessToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  setTokens({ accessToken, refreshToken }: AuthTokens): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    // Set cookie for middleware authentication check
    document.cookie = `viewhub_auth=true; path=/; max-age=${7 * 24 * 60 * 60}; SameSite=Lax`;
  }

  clearTokens(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    // Clear auth cookie
    document.cookie = 'viewhub_auth=; path=/; max-age=0';
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>('/auth/login', credentials);

    if (response.tokens?.accessToken && response.tokens?.refreshToken) {
      this.setTokens({
        accessToken: response.tokens.accessToken,
        refreshToken: response.tokens.refreshToken,
      });
    }

    return response;
  }

  async verifyTwoFactor(payload: TwoFactorPayload): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>('/auth/2fa/verify', payload);

    if (response.tokens?.accessToken && response.tokens?.refreshToken) {
      this.setTokens({
        accessToken: response.tokens.accessToken,
        refreshToken: response.tokens.refreshToken,
      });
    }

    return response;
  }

  async refreshTokens(): Promise<AuthTokens | null> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return null;

    try {
      const response = await api.post<AuthTokens>('/auth/refresh', { refreshToken });
      this.setTokens(response);
      return response;
    } catch {
      this.clearTokens();
      return null;
    }
  }

  async logout(): Promise<void> {
    const token = this.getAccessToken();
    const refreshToken = this.getRefreshToken();

    if (token && refreshToken) {
      try {
        await api.post('/auth/logout', { refreshToken }, { token });
      } catch {
        // Ignore logout errors
      }
    }

    this.clearTokens();
  }

  async getMe(): Promise<AuthUser> {
    const token = this.getAccessToken();
    if (!token) throw new Error('Not authenticated');

    return api.get<AuthUser>('/users/me', { token });
  }
}

export const authService = new AuthService();
export default authService;
